import { useCallback, useRef, useState } from "react";
import { classNames } from "primereact/utils";
import { useCashdeskOrders } from "../hooks/useCashdeskOrders";
import { CashdeskOrderDetailModal } from "./CashdeskOrderDetailModal";
import { IOrderViewModel } from "../models/cashdeskModels";
import { humanizePaymentType } from "../../../common/utilities/paymentUtility";
import { dateUtility } from "../../../common/infrastructure/utilities/dateUtility";
import { PaymentTypes } from "../../../common/enums";
import { IStudioSettingsModel } from "../../../common/models/IStudioSettingsModel";
import { Sidebar } from "primereact/sidebar";
import { Calendar } from "primereact/calendar";
import { SelectField } from "../../../common/components/forms/inputs/dropdown/SelectField";
import { Icon, IconType } from "../../../common/icons/Icon";
import { DefaultButton } from "../../../common/components/button/defaultButton/DefaultButton";
import styles from "./CashdeskOrdersTab.module.scss";

interface IPaymentOption {
  id: PaymentTypes;
  name: string;
}

interface ICashdeskOrdersTabProps {
  orders: ReturnType<typeof useCashdeskOrders>;
  paymentOptions: IPaymentOption[];
  studioSettings?: IStudioSettingsModel;
}

export const CashdeskOrdersTab = ({
  orders,
  paymentOptions,
  studioSettings,
}: ICashdeskOrdersTabProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [isFiltersSidebarOpen, setIsFiltersSidebarOpen] = useState(false);
  const closedAtRef = useRef(0);

  const hasActiveFilters =
    !!orders.dateFrom || !!orders.dateTo || orders.paymentFilter !== null;

  const activeFilterCount = [
    orders.dateFrom,
    orders.dateTo,
    orders.paymentFilter,
  ].filter(Boolean).length;

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 100;
    if (nearBottom && orders.hasMore && !orders.isLoading) {
      orders.loadMore();
    }
  }, [orders]);

  const formatOrderTime = (utcDate: string) => {
    return dateUtility.formatDateTime(utcDate);
  };

  const clearAllFilters = () => {
    orders.setDateFrom(null);
    orders.setDateTo(null);
    orders.setPaymentFilter(null);
  };

  const today = new Date();
  today.setHours(23, 59, 59, 999);

  const parseLocalDate = (dateStr: string): Date => {
    const [year, month, day] = dateStr.split("-").map(Number);
    return new Date(year, month - 1, day);
  };

  const formatLocalDate = (date: Date): string => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  return (
    <div className={styles.root}>
      {/* Search + filter button */}
      <div className={styles.toolbar}>
        <input
          className={styles.search_input}
          type="text"
          placeholder="Търси по № или клиент..."
          value={orders.search}
          onChange={(e) => orders.setSearch(e.target.value)}
        />
        <button
          className={classNames(styles.filter_btn, { [styles.filter_btn_active]: hasActiveFilters })}
          onClick={() => setIsFiltersSidebarOpen(true)}
          title="Филтри"
        >
          <Icon type={IconType.Filters} width="16px" height="16px" />
          {activeFilterCount > 0 && (
            <span className={styles.filter_badge}>{activeFilterCount}</span>
          )}
        </button>
      </div>

      {/* Orders list */}
      <div
        className={styles.list}
        ref={scrollRef}
        onScroll={handleScroll}
      >
        {orders.orders.length === 0 && !orders.isLoading && (
          <div className={styles.empty}>Няма намерени поръчки</div>
        )}

        {orders.orders.map((order: IOrderViewModel) => (
          <div
            key={order.id}
            className={classNames(styles.order_item, { [styles.order_item_deleted]: order.isDeleted })}
            onClick={() => {
              if (Date.now() - closedAtRef.current < 300) return;
              orders.setSelectedOrder(order);
            }}
          >
            <div className={styles.order_num}>{order.displayNumber}</div>
            <div className={styles.order_info}>
              <div className={styles.order_customer}>
                {order.customerName ?? "Анонимна"}
              </div>
              <div className={styles.order_time}>
                {formatOrderTime(order.createdOn)}
              </div>
            </div>
            <div className={styles.order_right}>
              <div className={styles.order_amount}>
                {order.total.toFixed(2)} €
              </div>
              <div className={styles.order_method}>
                {humanizePaymentType(order.paymentTypeId)}
              </div>
            </div>
          </div>
        ))}

        {orders.isLoading && (
          <div className={styles.loading}>Зареждане...</div>
        )}
      </div>

      {/* Filter sidebar */}
      <Sidebar
        visible={isFiltersSidebarOpen}
        position="right"
        onHide={() => setIsFiltersSidebarOpen(false)}
        className={styles.sidebar}
        header={<h3 className={styles.sidebar_title}>Филтри</h3>}
      >
        <div className={styles.sidebar_content}>
          <div className={styles.sidebar_section}>
            <div className={styles.sidebar_section_title}>Период</div>
            <div className={styles.sidebar_field}>
              <label className={styles.sidebar_label}>От</label>
              <div className={styles.sidebar_date_wrap}>
                <Calendar
                  value={orders.dateFrom ? parseLocalDate(orders.dateFrom) : null}
                  onChange={(e) => {
                    const d = e.value as Date | null;
                    orders.setDateFrom(d ? formatLocalDate(d) : null);
                  }}
                  maxDate={today}
                  dateFormat="dd/mm/yy"
                  showIcon
                  placeholder="Избери дата"
                  className={styles.sidebar_calendar}
                  touchUI
                />
                {orders.dateFrom && (
                  <button
                    className={styles.sidebar_date_clear}
                    onClick={() => orders.setDateFrom(null)}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
            <div className={styles.sidebar_field}>
              <label className={styles.sidebar_label}>До</label>
              <div className={styles.sidebar_date_wrap}>
                <Calendar
                  value={orders.dateTo ? parseLocalDate(orders.dateTo) : null}
                  onChange={(e) => {
                    const d = e.value as Date | null;
                    orders.setDateTo(d ? formatLocalDate(d) : null);
                  }}
                  maxDate={today}
                  dateFormat="dd/mm/yy"
                  showIcon
                  placeholder="Избери дата"
                  className={styles.sidebar_calendar}
                  touchUI
                />
                {orders.dateTo && (
                  <button
                    className={styles.sidebar_date_clear}
                    onClick={() => orders.setDateTo(null)}
                    type="button"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className={styles.sidebar_separator} />

          <div className={styles.sidebar_section}>
            <div className={styles.sidebar_section_title}>Плащане</div>
            <SelectField
              options={paymentOptions}
              value={orders.paymentFilter ?? undefined}
              onChange={(val) =>
                orders.setPaymentFilter(val ? (val as PaymentTypes) : null)
              }
              defaultValue="Всички плащания"
              label="Начин на плащане"
              width="100%"
              includeId={false}
              className={styles.sidebar_dropdown}
            />
          </div>

          <div className={styles.sidebar_actions}>
            <DefaultButton
              label="Изчисти филтрите"
              design="link"
              onClick={clearAllFilters}
              disabled={!hasActiveFilters}
              width="auto"
            />
            <DefaultButton
              label="Приложи"
              design="accent"
              onClick={() => setIsFiltersSidebarOpen(false)}
              width="100%"
            />
          </div>
        </div>
      </Sidebar>

      {/* Order detail modal */}
      <CashdeskOrderDetailModal
        visible={!!orders.selectedOrder}
        order={orders.selectedOrder}
        studioSettings={studioSettings}
        onClose={() => {
          closedAtRef.current = Date.now();
          orders.setSelectedOrder(null);
        }}
        onToggleDeletion={orders.toggleDeletionStatus}
      />
    </div>
  );
};
